import { z } from "zod";
import { HtsReadinessPillarKey } from "./constants";
import { HtsReadinessPillarConfig, HtsReadinessPillarConfigSchema, ScoringDimension } from "./schemas";
import { lifeSkills } from "./htsReadinessPillars/lifeSkills";
import { workExperience } from "./htsReadinessPillars/workExperience";
import { education } from "./htsReadinessPillars/education";
import { volunteering } from "./htsReadinessPillars/volunteering";
import { HtsIntegrityDimensionKey, integrity } from "./htsReadinessPillars/integrity";
import { fitness } from "./htsReadinessPillars/fitness";
import { jobFit } from "./htsReadinessPillars/jobFit";
import { motivation } from "./htsReadinessPillars/motivation";
import { leadership } from "./htsReadinessPillars/leadership";
import { specialSkills } from "./htsReadinessPillars/specialSkills";

const htsReadinessPillars: HtsReadinessPillarConfig[] = [
  lifeSkills,
  workExperience,
  education,
  volunteering,
  integrity,
  fitness,
  jobFit,
  motivation,
  leadership,
  specialSkills,
];

/**
 * One entry per Readiness Score pillar (Evaltick Readiness Scoring Rubric) —
 * each pillar's dimension weights and 0/25/50/75/100 anchor tables. The
 * single source of truth both htsReadinessScorer's prompt and
 * htsReadinessScoring.ts's pillar-score math read from. Mirrors
 * HTS_CLASSIFICATION_TOPICS' one-file-per-topic convention.
 */
export const HTS_READINESS_PILLARS_CONFIG: HtsReadinessPillarConfig[] = z
  .array(HtsReadinessPillarConfigSchema)
  .parse(htsReadinessPillars);

export const HTS_READINESS_PILLAR_CONFIG_BY_KEY: Record<string, HtsReadinessPillarConfig | undefined> =
  Object.fromEntries(HTS_READINESS_PILLARS_CONFIG.map((pillar) => [pillar.key, pillar]));

/**
 * The dimensions htsReadinessScorer is actually asked to score for a pillar.
 * Every pillar hands the model its whole dimension set except Integrity, whose
 * `conductHistory` and `honestyCompleteness` are computed deterministically in
 * code from Section B's own output and never modelled (see
 * htsReadinessPillars/integrity.ts) — the model only ever sees
 * `insightAccountability` there.
 *
 * Lives here rather than inline in htsReadinessScoring.ts so anything
 * rebuilding that prompt — the eval fixtures and contract scorers included —
 * sends the same dimension set production does, without importing the service
 * (and its Prisma dependency).
 */
export const modelScoredDimensions = (pillarConfig: HtsReadinessPillarConfig): ScoringDimension[] =>
  pillarConfig.key === HtsReadinessPillarKey.INTEGRITY
    ? pillarConfig.dimensions.filter((d) => d.key === HtsIntegrityDimensionKey.InsightAccountability)
    : pillarConfig.dimensions;

// Only the pillars whose individual dimensions are addressed by key from code
// (resolveEffectiveDimensionWeights, htsReadinessScoring.ts) publish their keys
// — every other pillar's dimensions are only ever iterated, never named.
export { HtsIntegrityDimensionKey } from "./htsReadinessPillars/integrity";
export { HtsEducationDimensionKey } from "./htsReadinessPillars/education";
export { HtsFitnessDimensionKey } from "./htsReadinessPillars/fitness";
